<script setup>
import { router } from '@inertiajs/vue3';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const { module, moduleTypes } = defineProps({
    module: {
        required: false,
        type: Object,
    },
    moduleTypes: {
        required: true,
        type: Array,
    },
});

const { route } = useRoute();

const form = reactive({
    name: module ? module.name : null,
    description: module ? module.description : null,
    type: module
        ? moduleTypes.find((type) => type.value === module.type)
        : null,
    emptyDescription: module ? module.emptyDescription : null,
    descriptionOnServicesAndModules: module
        ? module.descriptionOnServicesAndModules
        : null,
});

const isSubmitting = ref(false);

const validationSchema = object({
    name: string().required('Ce champ est obligatoire.'),
    description: string().nullable(),
    type: object().nullable(),
    emptyDescription: string().nullable(),
    descriptionOnServicesAndModules: string().nullable(),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(route('front.postModule', { module: module.key }), {
            name: form.name,
            type: form.type?.value,
            description: form.description,
            emptyDescription: form.emptyDescription,
            descriptionOnServicesAndModules:
                form.descriptionOnServicesAndModules,
        })
        .then(() => {
            toastSaved();

            router.visit(route('front.updateModule', { module: module.key }));
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="2">
                    <InputField
                        v-model="form.name"
                        label="Nom"
                        placeholder="Nom"
                        field-name="name"
                        field-id="name"
                        required
                    />

                    <ListboxField
                        v-model="form.type"
                        field-id="type"
                        field-name="type"
                        label="Type"
                        :options="moduleTypes"
                    >
                        <template #buttonContent>
                            {{ form.type.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="2">
                    <EditorField
                        v-model="form.description"
                        label="Description"
                        field-name="description"
                        field-id="description"
                        size="small"
                    />

                    <EditorField
                        v-model="form.emptyDescription"
                        label="Descripton vide"
                        field-name="emptyDescription"
                        field-id="emptyDescription"
                        size="small"
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="1">
                    <EditorField
                        v-model="form.descriptionOnServicesAndModules"
                        :label="'Description visible dans le module \'Services & modules\''"
                        field-name="descriptionOnServicesAndModules"
                        field-id="descriptionOnServicesAndModules"
                        size="small"
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors" />
    </FormRoot>
</template>
